import React from "react";
import Select, { Props as SelectProps, components } from "react-select";

interface BaseOption {
    id: number | string;
    [key: string]: any;
}

interface OptionType<T> {
    value: number | string;
    label: string;
    data: T;
}

interface Props<T extends BaseOption>
    extends Omit<SelectProps<OptionType<T>, false>, "options" | "onChange"> {
    items?: T[];
    labelKey: keyof T;
    onSelect: (item: T | undefined) => void;
    customIcons?: {
        searchIcon?: React.ReactNode;
        dropdownIcon?: React.ReactNode;
    };
    disabled?: boolean;
    maxMenuHeight?: number;
}

export const SearchableDropdown = <T extends BaseOption>({
    items,
    labelKey,
    onSelect,
    customIcons,
    placeholder = "Pretraga...",
    isValid,
    errorMessage,
    disabled = false,
    maxMenuHeight = 140, 
    ...props
}: Props<T>) => {
    const options: OptionType<T>[] = React.useMemo(
        () =>
            items
                ? items.map(item => ({
                      value: item.id,
                      label: String(item[labelKey]),
                      data: item,
                  }))
                : [],
        [items, labelKey]
    );

    const handleChange = React.useCallback(
        (option: OptionType<T> | null) => {
            onSelect(option?.data);
        },
        [onSelect]
    );

    const DropdownIndicator = React.useCallback(
        (props: any) =>
            customIcons ? (
                <components.DropdownIndicator {...props}>
                    {customIcons?.dropdownIcon}
                </components.DropdownIndicator>
            ) : (
                <DropdownIndicator />
            ),
        [customIcons?.dropdownIcon]
    );

    const Control = React.useCallback(
        ({ children, ...props }: any) => (
            <>
                <components.Control {...props}>
                    <div
                        className={`control-container ${
                            customIcons?.searchIcon ? "has-search-icon" : ""
                        }`}
                    >
                        {customIcons?.searchIcon && (
                            <div className="search-icon-wrapper">{customIcons.searchIcon}</div>
                        )}
                        {children}
                    </div>
                </components.Control>
                {!isValid && <p className="error-message">{errorMessage}</p>}
            </>
        ),
        [customIcons?.searchIcon]
    );

    return (
        <Select<OptionType<T>>
            className={`searchable-dropdown ${disabled ? "disabled" : ""}`}
            classNamePrefix="searchable-dropdown"
            options={options}
            onChange={handleChange}
            placeholder={placeholder}
            components={{
                DropdownIndicator,
                Control,
            }}
            isSearchable
            isDisabled={disabled}
            maxMenuHeight={maxMenuHeight} 
            {...props}
        />
    );
};

export default SearchableDropdown;
